import React, { Component } from 'react';
import { node, bool } from 'prop-types';
import {
    PREVENT_CLOSE_CLASS,
    LIFECYCLE_HOOKS,
    SHOULD_PREVENT_CLOSE_ACTION
} from './constants';

const isFunction = (fn) => typeof fn === 'function';

const safeRun = (fn, ...args) => {
    if (!isFunction(fn)) {
        return;
    }

    return fn(...args);
};

class Toggler extends Component {
    constructor(props) {
        super(props);

        this.state = {
            isOpen: props.defaultOpen || false
        };

        [
            LIFECYCLE_HOOKS.WILL_OPEN,
            LIFECYCLE_HOOKS.DID_OPEN,
            LIFECYCLE_HOOKS.WILL_CLOSE,
            LIFECYCLE_HOOKS.DID_CLOSE,
            SHOULD_PREVENT_CLOSE_ACTION
        ].forEach((fn) => {
            if (isFunction(this[fn])) {
                this[fn] = this[fn].bind(this);
            } else if (isFunction(props[fn])) {
                this[fn] = props[fn].bind(this);
            }
        }, this);
    }

    componentDidMount() {
        const { isOpen } = this.state;

        if (isOpen) {
            this.bindClickListener();
        }
    }

    componentWillUnmount() {
        this.removeClickListener();
    }

    onBodyClick = (e) => {
        const { isOpen } = this.state;
        const { target } = e;

        if (!isOpen) { return null; }

        if (isFunction(this.shouldPreventCloseAction)) {
            if (this.shouldPreventCloseAction(e) === true) { return null; }
        }

        if (
            this.shouldPreventPropagationByClassName(target) ||
            this.shouldPreventPropagationByElement(target)
        ) {
            return null;
        }

        this.close(e);
    }

    notPropagatingElements = new Set();

    open = async(e) => {
        if (e && e.persist) {
            e.persist();
        }

        const result = safeRun(this[LIFECYCLE_HOOKS.WILL_OPEN], e);

        if (result instanceof Promise) {
            await result;
        }

        this.setState({ isOpen: true }, () => {
            this.bindClickListener();
            safeRun(this[LIFECYCLE_HOOKS.DID_OPEN], e);
        });
    };

    close = async(e) => {
        if (e && e.persist) {
            e.persist();
        }

        const result = safeRun(this[LIFECYCLE_HOOKS.WILL_CLOSE], e);

        if (result instanceof Promise) {
            await result;
        }

        this.setState({ isOpen: false }, () => {
            this.removeClickListener();
            safeRun(this[LIFECYCLE_HOOKS.DID_CLOSE], e);
        });
    };

    toggle = async(e) => {
        const { isOpen } = this.state;
        const handler = isOpen ? this.close : this.open;

        await handler(e);
    }

    bindClickListener = () => {
        const { bindBodyClickListener } = this.props;

        if (bindBodyClickListener === false) { return; }

        document.body.addEventListener('click', this.onBodyClick);
    };

    removeClickListener = () => document.body.removeEventListener('click', this.onBodyClick);

    shouldPreventPropagationByClassName = (element) => {
        if (!element || !element.classList) { return false; }

        return element.classList.contains(PREVENT_CLOSE_CLASS);
    }

    shouldPreventPropagationByElement = (element) => {
        if (!element) { return false; }
        if (!element.contains) { return false; }
        if (!this.notPropagatingElements.size) { return false; }

        if (this.notPropagatingElements.has(element)) {
            return true;
        }

        for (const el of this.notPropagatingElements) {
            if (el.contains && el.contains(element)) {
                return true;
            }
        }

        return false;
    }

    preventCloseAction = (ref) => {
        if (!ref) { return;}
        if (this.notPropagatingElements.has(ref)) { return null; }

        this.notPropagatingElements.add(ref);
    }

    render() {
        const { children } = this.props;

        if (!['undefined', 'function'].includes(typeof children)) {
            throw new TypeError('Toggler: `children` must be a function');
        }

        const { close, open, toggle, preventCloseAction } = this;
        const { isOpen } = this.state;

        const renderFunc = this.toggleContent ? this.toggleContent.bind(this) : children;

        if (!renderFunc) {
            return null;
        }

        return renderFunc({ isOpen, close, open, toggle, preventCloseAction });
    }
}

Toggler.propTypes = {
    children: node,
    bindBodyClickListener: bool,
    defaultOpen: bool
};

Toggler.defaultProps = {
    defaultOpen: false
};

Object.assign(Toggler, {
    PREVENT_CLOSE_CLASS
});

export default Toggler;
